// Analytics Event Constants
export const ANALYTICS_EVENTS = {
  // Notification Events
  NOTIFICATION_RECEIVED: 'notification_received',
  NOTIFICATION_CLICKED: 'notification_clicked',
  NOTIFICATION_POLLING: 'notification_polling',
  NOTIFICATION_INTERACTION: 'notification_interaction',
  NOTIFICATION_CONVERSION: 'notification_conversion',

  // Tour Events
  TOUR_INTERACTION: 'tour_interaction',
  HEADER_TOUR_STEP: 'header_tour_step',
  NOTIFICATIONS_TOUR_STEP: 'notifications_tour_step',
  SETTINGS_TOUR_STEP: 'settings_tour_step',
  TOUR_NAVIGATION: 'tour_navigation',

  // Watch List Events
  WATCH_LIST_UPDATED: 'watch_list_updated',
  WATCH_LIST_ADD: 'watch_list_add',
  WATCH_LIST_REMOVE: 'watch_list_remove',
  WATCH_LIST_REMOVE_ALL: 'watch_list_remove_all',

  // Settings Events
  SETTINGS_UPDATED: 'settings_updated',

  // Navigation Events
  NAVIGATION_CLICKED: 'navigation_clicked',

  // Form Help Events
  FORM_HELP_ACCESSED: 'form_help_accessed',
  FORM_HELP_INTERACTION: 'form_help_interaction',

  // Cross-tab Events
  CROSS_TAB_SYNC: 'cross_tab_sync',

  // Error Events
  NOTIFICATION_API_ERROR: 'notification_api_error',
  NOTIFICATION_UI_ERROR: 'notification_ui_error',
  WATCH_LIST_ERROR: 'watch_list_error',
  TOUR_SYSTEM_ERROR: 'tour_system_error',
  SETTINGS_UPDATE_ERROR: 'settings_update_error',

  // Feature Usage Events
  FEATURE_USED: 'feature_used',
} as const;

// Type for analytics events
export type AnalyticsEventType =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

// Notification Types (from backend constants)
export const NOTIFICATION_TYPES = {
  CERTIFICATION_APPROVED: 'certification_approved',
  CERTIFICATION_REJECTED: 'certification_rejected',
  CERTIFICATION_PENDING: 'certification_pending',
  CERTIFICATION_EXPIRED: 'certification_expired',
  CERTIFICATION_RENEWED: 'certification_renewed',
  CERTIFICATION_CANCELLED: 'certification_cancelled',
  CERTIFICATION_UPDATED: 'certification_updated',
  CERTIFICATION_CREATED: 'certification_created',
  CERTIFICATION_DELETED: 'certification_deleted',
  CERTIFICATION_ASSIGNED: 'certification_assigned',
  CERTIFICATION_UNASSIGNED: 'certification_unassigned',
  CERTIFICATION_COMMENTED: 'certification_commented',
  CERTIFICATION_ATTACHMENT_ADDED: 'certification_attachment_added',
  CERTIFICATION_ATTACHMENT_REMOVED: 'certification_attachment_removed',
  CERTIFICATION_ATTACHMENT_UPDATED: 'certification_attachment_updated',
  CERTIFICATION_ATTACHMENT_DOWNLOADED: 'certification_attachment_downloaded',
  CERTIFICATION_ATTACHMENT_VIEWED: 'certification_attachment_viewed',
  CERTIFICATION_ATTACHMENT_PRINTED: 'certification_attachment_printed',
  CERTIFICATION_ATTACHMENT_EMAILED: 'certification_attachment_emailed',
  CERTIFICATION_ATTACHMENT_FAXED: 'certification_attachment_faxed',
  CERTIFICATION_ATTACHMENT_MAILED: 'certification_attachment_mailed',
  CERTIFICATION_ATTACHMENT_COURIERED: 'certification_attachment_couriered',
  CERTIFICATION_ATTACHMENT_HAND_DELIVERED:
    'certification_attachment_hand_delivered',
  CERTIFICATION_ATTACHMENT_OTHER: 'certification_attachment_other',
  CERTIFICATION_ATTACHMENT_UPLOADED: 'certification_attachment_uploaded',
  CERTIFICATION_ATTACHMENT_DELETED: 'certification_attachment_deleted',
  CERTIFICATION_ATTACHMENT_RESTORED: 'certification_attachment_restored',
  CERTIFICATION_ATTACHMENT_ARCHIVED: 'certification_attachment_archived',
  CERTIFICATION_ATTACHMENT_UNARCHIVED: 'certification_attachment_unarchived',
  CERTIFICATION_ATTACHMENT_MOVED: 'certification_attachment_moved',
  CERTIFICATION_ATTACHMENT_COPIED: 'certification_attachment_copied',
  CERTIFICATION_ATTACHMENT_RENAMED: 'certification_attachment_renamed',
  CERTIFICATION_ATTACHMENT_TAGGED: 'certification_attachment_tagged',
  CERTIFICATION_ATTACHMENT_UNTAGGED: 'certification_attachment_untagged',
  CERTIFICATION_ATTACHMENT_SHARED: 'certification_attachment_shared',
  CERTIFICATION_ATTACHMENT_UNSHARED: 'certification_attachment_unshared',
  CERTIFICATION_ATTACHMENT_LOCKED: 'certification_attachment_locked',
  CERTIFICATION_ATTACHMENT_UNLOCKED: 'certification_attachment_unlocked',
  CERTIFICATION_ATTACHMENT_PASSWORD_PROTECTED:
    'certification_attachment_password_protected',
  CERTIFICATION_ATTACHMENT_PASSWORD_REMOVED:
    'certification_attachment_password_removed',
  CERTIFICATION_ATTACHMENT_ENCRYPTED: 'certification_attachment_encrypted',
  CERTIFICATION_ATTACHMENT_DECRYPTED: 'certification_attachment_decrypted',
  CERTIFICATION_ATTACHMENT_COMPRESSED: 'certification_attachment_compressed',
  CERTIFICATION_ATTACHMENT_DECOMPRESSED:
    'certification_attachment_decompressed',
  CERTIFICATION_ATTACHMENT_CONVERTED: 'certification_attachment_converted',
  CERTIFICATION_ATTACHMENT_OPTIMIZED: 'certification_attachment_optimized',
  CERTIFICATION_ATTACHMENT_RESIZED: 'certification_attachment_resized',
  CERTIFICATION_ATTACHMENT_CROPPED: 'certification_attachment_cropped',
  CERTIFICATION_ATTACHMENT_ROTATED: 'certification_attachment_rotated',
  CERTIFICATION_ATTACHMENT_FLIPPED: 'certification_attachment_flipped',
  CERTIFICATION_ATTACHMENT_FILTERED: 'certification_attachment_filtered',
  CERTIFICATION_ATTACHMENT_ADJUSTED: 'certification_attachment_adjusted',
  CERTIFICATION_ATTACHMENT_ENHANCED: 'certification_attachment_enhanced',
  // removed duplicate CERTIFICATION_ATTACHMENT_RESTORED
  CERTIFICATION_ATTACHMENT_REPAIRED: 'certification_attachment_repaired',
  CERTIFICATION_ATTACHMENT_CLEANED: 'certification_attachment_cleaned',
  CERTIFICATION_ATTACHMENT_VALIDATED: 'certification_attachment_validated',
  CERTIFICATION_ATTACHMENT_VERIFIED: 'certification_attachment_verified',
  CERTIFICATION_ATTACHMENT_APPROVED: 'certification_attachment_approved',
  CERTIFICATION_ATTACHMENT_REJECTED: 'certification_attachment_rejected',
  CERTIFICATION_ATTACHMENT_PENDING: 'certification_attachment_pending',
  CERTIFICATION_ATTACHMENT_EXPIRED: 'certification_attachment_expired',
  CERTIFICATION_ATTACHMENT_RENEWED: 'certification_attachment_renewed',
  CERTIFICATION_ATTACHMENT_CANCELLED: 'certification_attachment_cancelled',
  CERTIFICATION_ATTACHMENT_CREATED: 'certification_attachment_created',
  // removed duplicate CERTIFICATION_ATTACHMENT_DELETED
  CERTIFICATION_ATTACHMENT_ASSIGNED: 'certification_attachment_assigned',
  CERTIFICATION_ATTACHMENT_UNASSIGNED: 'certification_attachment_unassigned',
  CERTIFICATION_ATTACHMENT_COMMENTED: 'certification_attachment_commented',
} as const;

export type NotificationType =
  (typeof NOTIFICATION_TYPES)[keyof typeof NOTIFICATION_TYPES];

// Notification Sources
export const NOTIFICATION_SOURCES = {
  SYSTEM: 'system',
  USER: 'user',
  API: 'api',
  WEBHOOK: 'webhook',
  EMAIL: 'email',
  SMS: 'sms',
  PUSH: 'push',
  IN_APP: 'in_app',
  DASHBOARD: 'dashboard',
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
  TABLET: 'tablet',
  WEB: 'web',
  API_WEBHOOK: 'api_webhook',
  EMAIL_WEBHOOK: 'email_webhook',
  SMS_WEBHOOK: 'sms_webhook',
  PUSH_WEBHOOK: 'push_webhook',
  IN_APP_WEBHOOK: 'in_app_webhook',
  DASHBOARD_WEBHOOK: 'dashboard_webhook',
  MOBILE_WEBHOOK: 'mobile_webhook',
  DESKTOP_WEBHOOK: 'desktop_webhook',
  TABLET_WEBHOOK: 'tablet_webhook',
  WEB_WEBHOOK: 'web_webhook',
} as const;

export type NotificationSource =
  (typeof NOTIFICATION_SOURCES)[keyof typeof NOTIFICATION_SOURCES];

// Tour Actions
export const TOUR_ACTIONS = {
  STARTED: 'started',
  COMPLETED: 'completed',
  PAUSED: 'paused',
  RESUMED: 'resumed',
  SKIPPED: 'skipped',
  CANCELLED: 'cancelled',
  STEP_COMPLETED: 'step_completed',
  STEP_SKIPPED: 'step_skipped',
  STEP_BACK: 'step_back',
  STEP_FORWARD: 'step_forward',
  HELP_REQUESTED: 'help_requested',
  FEEDBACK_SUBMITTED: 'feedback_submitted',
} as const;

export type TourAction = (typeof TOUR_ACTIONS)[keyof typeof TOUR_ACTIONS];

// Navigation Sections
export const NAVIGATION_SECTIONS = {
  DASHBOARD: 'dashboard',
  SETTINGS: 'settings',
  PROFILE: 'profile',
  HELP: 'help',
  NOTIFICATIONS: 'notifications',
} as const;

export type NavigationSection =
  (typeof NAVIGATION_SECTIONS)[keyof typeof NAVIGATION_SECTIONS];

// Form Help Actions
export const FORM_HELP_ACTIONS = {
  ACCESSED: 'accessed',
  SEARCHED: 'searched',
  VIEWED: 'viewed',
  COPIED: 'copied',
  PRINTED: 'printed',
  SHARED: 'shared',
  BOOKMARKED: 'bookmarked',
  RATED: 'rated',
  FEEDBACK: 'feedback',
} as const;

export type FormHelpAction =
  (typeof FORM_HELP_ACTIONS)[keyof typeof FORM_HELP_ACTIONS];

// Error Types
export const ERROR_TYPES = {
  FETCH_FAILED: 'fetch_failed',
  NETWORK_ERROR: 'network_error',
  TIMEOUT_ERROR: 'timeout_error',
  VALIDATION_ERROR: 'validation_error',
  AUTHENTICATION_ERROR: 'authentication_error',
  AUTHORIZATION_ERROR: 'authorization_error',
  SERVER_ERROR: 'server_error',
  CLIENT_ERROR: 'client_error',
  UNKNOWN_ERROR: 'unknown_error',
} as const;

export type ErrorType = (typeof ERROR_TYPES)[keyof typeof ERROR_TYPES];

// Watch List Action Types
export const WATCH_LIST_ACTIONS = {
  ADD_TO_WATCHLIST: 'add_to_watchlist',
  REMOVE_FROM_WATCHLIST: 'remove_from_watchlist',
  REMOVE_ALL_FROM_WATCHLIST: 'remove_all_from_watchlist',
} as const;

// Watch List Source Types
export const WATCH_LIST_SOURCES = {
  TEAM_RENTERS: 'team_renters',
  HEADER: 'header',
  CERTIFICATION_PAGE: 'certification_page',
} as const;
